/* components/OperatingCostsGrid.tsx
 * Khula Funding Desk – Operating- & Production-cost grid
 * Flexible fiscal window 18 months (Aug-25 → Jan-27)
 * ---------------------------------------------------------------------------
 *  ✦ 2025-08-07 patch – *only* two functional additions
 *    1. buildCostRows() understands legacy { total:"undefined", months:[] }
 *    2. handleSave() always writes monthlyValues **and** a numeric total
 * ---------------------------------------------------------------------------
 */

import React, {useMemo, useRef, useState, useEffect} from 'react';
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  ColDef,
  GridApi,
  IRowNode,
  RowStyle,
  RowClassParams,
  ITooltipParams,
} from 'ag-grid-community';
import {Button} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';
import {
  apiUpsertFdOperatingCost,
  apiDeleteFdOperatingCost,
} from 'constants/Api';
import {
  IGenericType,
  IFdOperatingCost,
  IFdProductionCost,
} from 'constants/interfaces';
import {
  reduxSetOperatingCosts,
  reduxRemoveOperatingCost,
} from 'appredux/actions';
import {RootState} from 'appredux/reducers';
import {
  lshParseOperatingCostMonthlyCosts,
  lshSerializeOperatingCostMonthlyCosts,
} from '../../utils/operatingCostMonthlyCosts';
import './OperatingCostsGrid.scss';

/* ───────── fiscal-window config ───────── */
const FISCAL_START = {startMonth: 7, startYear: 2025}; // Aug-25
const MONTH_WINDOW = 18; // Aug-25 → Jan-27

const CAL = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;
type CalMonth = (typeof CAL)[number];

/* generate keys with correct year roll-over */
const makeFiscal = (
  {startMonth, startYear}: {startMonth: number; startYear: number},
  len: number,
) => {
  const out: {key: string; m: CalMonth; y: number}[] = [];
  for (let i = 0; i < len; i++) {
    const idx = (startMonth + i) % 12;
    const y = startYear + Math.floor((startMonth + i) / 12);
    const m = CAL[idx] as CalMonth;
    out.push({key: `${m}${String(y).slice(-2)}`, m, y});
  }
  return out;
};

const FISCAL = makeFiscal(FISCAL_START, MONTH_WINDOW);
export type FiscalKey = (typeof FISCAL)[number]['key'];

type FiscalDict = {[K in FiscalKey]: number};
export const BLANK: FiscalDict = Object.fromEntries(
  FISCAL.map((f) => [f.key, 0]),
) as FiscalDict;

/* ───────── row shape ───────── */
interface GridRowMeta {
  id?: number;
  rowKind: '' | 'revenue' | 'cost' | 'prod';
  expenseId: number;
  label?: string;
  notes?: string;
  summary?: boolean;
  css?: 'sum-green' | 'sum-red' | 'sum-grey' | 'sum-blue';
}
export type GridRow = GridRowMeta & FiscalDict;

/* ───────── helpers ───────── */
const fiscalKey = (d: Date) =>
  `${CAL[d.getMonth()]}${String(d.getFullYear()).slice(-2)}` as FiscalKey;

const tooltipGetter = (p: ITooltipParams) => {
  if (p.data?.rowKind === 'revenue')
    return 'Edit Production Plan to change revenue assumptions';
  if (p.data?.rowKind === 'prod')
    return 'Update Programme Costing to change this';
  return undefined;
};

/* ───────── row builders ───────── */

const buildCostRows = (
  ocs: IFdOperatingCost[],
  expenses: IGenericType[],
): GridRow[] =>
  ocs.map((oc) => {
    const base = {
      ...BLANK,
      rowKind: 'cost',
      expenseId: Number(oc.expense?.id ?? expenses[0]?.id),
      label: oc.expense?.name,
      notes: oc.notes ?? '',
    } as GridRow;

    if (oc.id) base.id = Number(oc.id);

    try {
      const {monthlyValues} = lshParseOperatingCostMonthlyCosts(
        oc.monthlyCosts,
      );
      Object.entries(monthlyValues).forEach(([k, v]) => {
        const key = k.toLowerCase() as FiscalKey;
        if (key in base) base[key] = Number(v) || 0;
      });
    } catch {
      /* ignore malformed JSON */
    }
    return base;
  });

const buildProdCostRows = (pcs: IFdProductionCost[]): GridRow[] =>
  pcs.flatMap((pc) => {
    if (!pc.plantingDate) return [];
    const when = new Date(pc.plantingDate);
    when.setMonth(when.getMonth() - 1);
    const key = fiscalKey(when);
    if (!(key in BLANK)) return [];

    const base = {
      ...BLANK,
      rowKind: 'prod',
      expenseId: 0,
      notes: pc.notes ?? '',
    } as GridRow;

    return [
      {label: `${pc.crop?.name} Seed`, value: pc.seed},
      {label: `${pc.crop?.name} Fertiliser`, value: pc.fertiliser},
      {label: `${pc.crop?.name} Chemical`, value: pc.chemical},
    ]
      .filter((x) => x.value && x.value > 0)
      .map((x) => ({...base, [key]: x.value, label: x.label} as GridRow));
  });

/* ───────── aggregation helpers ───────── */
const sumRows = (rows: GridRow[]): FiscalDict => {
  const s: FiscalDict = {...BLANK};
  rows.forEach((r) =>
    FISCAL.forEach((f) => {
      s[f.key] += r[f.key];
    }),
  );
  return s;
};

/* replace the existing makeSumRow helper with this version */

const makeSumRow = (
  label: string,
  data: FiscalDict,
  css: GridRow['css'],
): GridRow =>
  ({
    ...BLANK,
    ...data,
    summary: true,
    label,
    css,
    rowKind: '', // summary rows aren’t editable
    expenseId: 0,
  } as GridRow); // ← cast fixes TS2322

const collectRows = (api: GridApi): GridRow[] => {
  const out: GridRow[] = [];
  api.forEachNodeAfterFilterAndSort((n: IRowNode<any>, _index: number) => {
    if (n.data && !n.data.summary) out.push(n.data as GridRow);
  });
  return out;
};

const buildBottomRows = (rows: GridRow[]): GridRow[] => {
  const rev = rows.filter((r) => r.rowKind === 'revenue');
  const cost = rows.filter((r) => r.rowKind !== 'revenue');

  const net: FiscalDict = {...BLANK};
  const run: FiscalDict = {...BLANK};
  let acc = 0;

  FISCAL.forEach((f) => {
    net[f.key] =
      rev.reduce((s, r) => s + r[f.key], 0) -
      cost.reduce((s, r) => s + r[f.key], 0);
    acc += net[f.key];
    run[f.key] = acc;
  });

  return [
    makeSumRow('NET (Rev-Cost)', net, 'sum-grey'),
    makeSumRow('RUNNING BALANCE', run, 'sum-blue'),
  ];
};

/* ───────── component ───────── */
interface Props {
  applicationId: number;
  operatingCosts: IFdOperatingCost[];
  productionCosts: IFdProductionCost[];
  revenueRows: Omit<GridRow, 'id'>[];
  allExpenses: IGenericType[];
}

export default function OperatingCostsGrid({
  applicationId,
  operatingCosts,
  productionCosts,
  revenueRows,
  allExpenses,
}: Props) {
  const dispatch = useDispatch();
  const storeCosts = useSelector(
    (s: RootState) => s.operatingCosts.rows,
  ) as IFdOperatingCost[];

  /* prime slice */
  useEffect(() => {
    dispatch(reduxSetOperatingCosts(operatingCosts));
  }, [dispatch, operatingCosts]);

  const costRows = useMemo(
    () => buildCostRows(storeCosts, allExpenses),
    [storeCosts, allExpenses],
  );
  const prodRows = useMemo(
    () => buildProdCostRows(productionCosts),
    [productionCosts],
  );

  const bodyRows = useMemo<GridRow[]>(() => {
    const rev = revenueRows.map(
      (r) => ({...BLANK, ...r, rowKind: 'revenue'} as GridRow),
    );
    return [
      ...rev,
      makeSumRow('TOTAL REVENUE', sumRows(rev), 'sum-green'),
      makeSumRow('TOTAL COSTS', sumRows([...costRows, ...prodRows]), 'sum-red'),
      ...prodRows,
      ...costRows,
    ];
  }, [revenueRows, costRows, prodRows]);

  const [rowData, setRowData] = useState<GridRow[]>(bodyRows);
  useEffect(() => setRowData(bodyRows), [bodyRows]);

  const gridRef = useRef<AgGridReact>(null);
  const [saving, setSaving] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const rowH = 36;
  const headerH = 40;
  const pinned = buildBottomRows([]).length;
  const gridH = headerH + (rowData.length + pinned) * rowH;

  /* ───────── columns ───────── */
  const monthCols: ColDef[] = FISCAL.map((f) => ({
    headerName: f.key.toUpperCase(),
    field: f.key,
    minWidth: 90,
    maxWidth: 110,
    editable: (p) => p.data?.rowKind === 'cost',
    type: 'numericColumn',
    valueParser: (p) => Number(p.newValue) || 0,
    valueFormatter: ({value}) => (value ? value.toLocaleString() : ''),
    tooltipValueGetter: tooltipGetter,
  }));
  const expenseNames = useMemo(
    () => allExpenses.map((e) => e.name).filter(Boolean),
    [allExpenses],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Item',
        field: 'label',
        minWidth: 220,
        editable: (p) => p.data?.rowKind === 'cost',
        tooltipValueGetter: tooltipGetter,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: expenseNames,
          // ensure long lists can scroll
          cellEditorPopup: true,
          cellEditorPopupPosition: 'under',
        },
        valueSetter: (params) => {
          const label: string = params.newValue;
          const match = allExpenses.find((e) => e.name === label);
          params.data.label = label;
          if (match) params.data.expenseId = Number(match.id);
          return true;
        },
      },
      {
        headerName: 'Notes',
        field: 'notes',
        minWidth: 220,
        editable: (p) => p.data?.rowKind === 'cost',
        hide: !showNotes,
      },
      ...monthCols,
      {
        headerName: 'TOTAL',
        field: '_tot_',
        minWidth: 110,
        valueGetter: ({data}) => FISCAL.reduce((s, f) => s + data[f.key], 0),
        valueFormatter: ({value}) => (value ? value.toLocaleString() : ''),
      },
      {
        headerName: '',
        field: '_act_',
        width: 70,
        pinned: 'right',
        cellRenderer: (p: any) =>
          !p.data?.summary &&
          p.data?.rowKind === 'cost' && (
            <Button
              size="small"
              color="secondary"
              onClick={() => handleDelete(p.data)}
            >
              Del
            </Button>
          ),
      },
    ],
    [expenseNames, showNotes, allExpenses],
  );

  /* ───────── utils ───────── */
  const fitColumns = (a: GridApi) => a.sizeColumnsToFit();
  const setPinned = (a: GridApi) =>
    a.setPinnedBottomRowData(buildBottomRows(collectRows(a)));

  /* ───────── save ───────── */
  const handleSave = async () => {
    if (!gridRef.current?.api) return;

    const rows = collectRows(gridRef.current.api).filter(
      (r) => r.rowKind === 'cost',
    );

    setSaving(true);
    try {
      const nextCosts: IFdOperatingCost[] = [...storeCosts];

      for (const r of rows) {
        const monthlyValues = Object.fromEntries(
          FISCAL.map((f) => [f.key, r[f.key]]),
        );

        const dto = {
          id: r.id,
          applicationId,
          expenseId: r.expenseId,
          year: FISCAL_START.startYear,
          monthlyCosts: lshSerializeOperatingCostMonthlyCosts(
            monthlyValues,
            'monthly',
          ),
          notes: r.notes ?? '',
        };

        const saved = await apiUpsertFdOperatingCost(dto);
        const expenseObj = saved.expense ??
          allExpenses.find((e) => e.id === dto.expenseId) ?? {
            id: dto.expenseId,
            name: r.label ?? '',
          };

        const idx = nextCosts.findIndex(
          (c) => String(c.id) === String(saved.id),
        );
        const merged: IFdOperatingCost = {
          ...saved,
          monthlyCosts: dto.monthlyCosts,
          expense: expenseObj,
        } as any;
        if (idx >= 0) nextCosts[idx] = merged;
        else nextCosts.push(merged);
      }

      // Single redux update to avoid multiple re-renders/flicker
      dispatch(reduxSetOperatingCosts(nextCosts));
    } finally {
      setSaving(false);
      if (gridRef.current?.api) setPinned(gridRef.current.api);
    }
  };

  /* ───────── delete ───────── */
  const handleDelete = async (row: GridRow) => {
    if (!gridRef.current?.api) return;

    gridRef.current.api.applyTransaction({remove: [row]});
    setRowData((prev) => prev.filter((r) => r !== row));
    setPinned(gridRef.current.api);

    if (!row.id) return;

    try {
      const ok = await apiDeleteFdOperatingCost(row.id);
      if (ok) dispatch(reduxRemoveOperatingCost(row.id));
      else throw new Error('API returned false');
    } catch (err) {
      setRowData((prev) => [...prev, row]);
      setPinned(gridRef.current.api);
      console.error('Delete failed:', err);
    }
  };

  /* ───────── add ───────── */
  const handleAdd = () => {
    const blank = {
      ...BLANK,
      rowKind: 'cost',
      expenseId: Number(allExpenses[0]?.id ?? 0),
      label: allExpenses[0]?.name,
      notes: '',
    } as GridRow;

    gridRef.current?.api?.applyTransaction({add: [blank]});
    setRowData((prev) => [...prev, blank]);
    if (gridRef.current?.api) setPinned(gridRef.current.api);
  };

  /* ───────── row styling ───────── */
  const getRowStyle = (p: RowClassParams): RowStyle | undefined => {
    if (!p.data?.css) return undefined;
    const bold: RowStyle = {fontWeight: 'bold'};
    switch (p.data.css) {
      case 'sum-green':
        return {...bold, background: '#d1f2d1'};
      case 'sum-red':
        return {...bold, background: '#f5c7c7'};
      case 'sum-grey':
        return {...bold, background: '#e2e2e2'};
      case 'sum-blue':
        return {...bold, background: '#e8f1ff'};
      default:
        return undefined;
    }
  };

  /* ───────── render ───────── */
  return (
    <>
      <div
        className="ag-theme-alpine operating-costs-grid"
        style={{height: gridH}}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={{
            resizable: true,
            sortable: false,
            tooltipValueGetter: tooltipGetter,
          }}
          domLayout="autoHeight"
          rowHeight={rowH}
          tooltipShowDelay={0}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          suppressAggFuncInHeader
          getRowStyle={getRowStyle}
          onGridReady={({api, columnApi}) => {
            fitColumns(api);
            setPinned(api);
          }}
          onFirstDataRendered={({api, columnApi}) => {
            fitColumns(api);
            setPinned(api);
          }}
        />
      </div>

      <div className="grid-actions">
        <Button onClick={handleAdd}>Add line</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button onClick={() => setShowNotes((v) => !v)}>
          {showNotes ? 'Hide Notes' : 'Show Notes'}
        </Button>
      </div>
    </>
  );
}
